import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from './api';

function Tata1MGOptometryForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const OPTOMETRY_URL = '/technician/tata1mg/optometry/';
  const SUBMIT_SERVICE_URL = '/technician/submit/';

  const { patientId, patientName, technicianId, serviceId } = location.state || {};

  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [formData, setFormData] = useState({
    patient_unique_id: "",
    
    // WITHOUT GLASSES - Vision fields
    far_vision_right_wog: "",
    far_vision_left_wog: "",
    near_vision_right_wog: "",
    near_vision_left_wog: "",
    
    // WITHOUT GLASSES - Refraction (Right Eye)
    spherical_right_wog: "",
    cylindrical_right_wog: "",
    axis_right_wog: "",
    add_right_wog: "",
    
    // WITHOUT GLASSES - Refraction (Left Eye)
    spherical_left_wog: "",
    cylindrical_left_wog: "",
    axis_left_wog: "",
    add_left_wog: "",
    
    // WITH GLASSES - Vision fields
    far_vision_right_wg: "",
    far_vision_left_wg: "",
    near_vision_right_wg: "",
    near_vision_left_wg: "",
    
    // WITH GLASSES - Refraction (Right Eye)
    spherical_right_wg: "",
    cylindrical_right_wg: "",
    axis_right_wg: "",
    add_right_wg: "",
    
    // WITH GLASSES - Refraction (Left Eye)
    spherical_left_wg: "",
    cylindrical_left_wg: "",
    axis_left_wg: "",
    add_left_wg: "",
    
    // Color Vision
    color_vision_normal: false,
    color_vision_other: "",
    color_vision_remark: ""
  });

  // Common vision measurement options
  const visionOptions = [
    { value: "", display: "Select Vision" },
    { value: "6/6", display: "6/6 (Normal)" },
    { value: "6/9", display: "6/9" },
    { value: "6/12", display: "6/12" },
    { value: "6/18", display: "6/18" },
    { value: "6/24", display: "6/24" },
    { value: "6/36", display: "6/36" },
    { value: "6/60", display: "6/60" },
    { value: "3/60", display: "3/60" },
    { value: "1/60", display: "1/60" },
    { value: "HM", display: "Hand Movement" },
    { value: "CF", display: "Counting Fingers" },
    { value: "PL", display: "Perception of Light" },
    { value: "NPL", display: "No Perception of Light" }
  ];

  const nearVisionOptions = [
    { value: "", display: "Select Near Vision" },
    { value: "N/6", display: "N/6 (Normal)" },
    { value: "N/8", display: "N/8" },
    { value: "N/10", display: "N/10" },
    { value: "N/12", display: "N/12" },
    { value: "N/14", display: "N/14" },
    { value: "N/18", display: "N/18" },
    { value: "N/24", display: "N/24" },
    { value: "N/36", display: "N/36" },
    { value: "N/48", display: "N/48" },
    { value: "N/60", display: "N/60" }
  ];

  // Spherical power options
  const sphericalOptions = [
    { value: "", display: "Select Spherical Power" },
    { value: "Plano", display: "Plano" },
    ...Array.from({ length: 60 }, (_, i) => {
      const value = ((i + 1) * 0.25).toFixed(2);
      return { value: `+${value}`, display: `+${value}` };
    }),
    ...Array.from({ length: 60 }, (_, i) => {
      const value = ((i + 1) * 0.25).toFixed(2);
      return { value: `-${value}`, display: `-${value}` };
    })
  ];

  // Cylindrical power options
  const cylindricalOptions = [
    { value: "", display: "Select Cylindrical Power" },
    { value: "Plano", display: "Plano" },
    ...Array.from({ length: 24 }, (_, i) => {
      const value = ((i + 1) * 0.25).toFixed(2);
      return { value: `+${value}`, display: `+${value}` };
    }),
    ...Array.from({ length: 24 }, (_, i) => {
      const value = ((i + 1) * 0.25).toFixed(2);
      return { value: `-${value}`, display: `-${value}` };
    })
  ];

  // Axis options (1-180 degrees)
  const axisOptions = [
    { value: "", display: "Select Axis" },
    ...Array.from({ length: 180 }, (_, i) => {
      const value = i + 1;
      return { value: value.toString(), display: `${value}°` };
    })
  ];

  // Add power options
  const addOptions = [
    { value: "", display: "Select Add Power" },
    ...Array.from({ length: 12 }, (_, i) => {
      const value = ((i + 1) * 0.25).toFixed(2);
      return { value: `+${value}`, display: `+${value}` };
    })
  ];

  const fetchExistingOptometry = async () => {
    try {
        setLoading(true);

        const response = await api.get(`${OPTOMETRY_URL}${patientId}/`);
        console.log("📥 Existing optometry data:", response.data);

        if (response.data?.data) {
        setFormData((prev) => ({
            ...prev,
            ...response.data.data, // 🔥 pre-fill all fields
            patient_unique_id: patientId
        }));
        }
    } catch (err) {
        console.warn("ℹ️ No existing optometry data found");
    } finally {
        setLoading(false);
    }
    };

  useEffect(() => {
    if (!patientId || !patientName) {
      setError("Missing patient information. Please go back and try again.");
      return;
    }

    setPatient({
      patient_name: patientName,
      unique_patient_id: patientId
    });

    setFormData((prev) => ({ ...prev, patient_unique_id: patientId }));
    fetchExistingOptometry();
  }, [patientId, patientName]);

  const handleInputChange = (field, value) => {
    if (field === "color_vision_normal") {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
        color_vision_other: value ? "" : prev.color_vision_other
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const submitData = { ...formData, technician_id: technicianId };
      const preservedFields = ["patient_unique_id", "color_vision_normal"];

      Object.keys(submitData).forEach((key) => {
        if (!preservedFields.includes(key) && submitData[key] === '') {
          submitData[key] = null;
        }
      });

      console.log("📤 Submitting optometry data:", submitData);

      const response = await api.post(`${OPTOMETRY_URL}${patientId}/`, submitData);
      console.log("✅ Optometry data submitted:", response.data);

      if (patientId && technicianId && serviceId) {
        const completePayload = {
            patient_id: patientId,   // 🔥 KEY FIX
            technician_id: technicianId,
            service_id: serviceId,
        };

        const completeRes = await api.post(SUBMIT_SERVICE_URL, completePayload);
        console.log("✅ Service marked as completed:", completeRes.data);
      }

      setSuccess("Optometry data saved and service marked as completed!");
      setTimeout(() => navigate(-1), 2000);
    } catch (err) {
      console.error("❌ Submission error:", err);
      setError(err.response?.data?.message || err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const renderVisionSection = (title, suffix, bgColor) => (
    <div className={`${bgColor} p-6 rounded-lg shadow border-2 border-gray-200`}>
      <h2 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
        {title}
        <span className="ml-2 text-sm font-normal text-gray-600">
          {suffix === 'wog' ? '(Without Glasses)' : '(With Glasses)'}
        </span>
      </h2>
      
      {/* Far Vision */}
      <div className="mb-6">
        <h3 className="text-lg font-medium mb-3 text-gray-700">Far Vision Assessment</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">Right Eye</label>
            <select
              value={formData[`far_vision_right_${suffix}`]}
              onChange={(e) => handleInputChange(`far_vision_right_${suffix}`, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {visionOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.display}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">Left Eye</label>
            <select
              value={formData[`far_vision_left_${suffix}`]}
              onChange={(e) => handleInputChange(`far_vision_left_${suffix}`, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {visionOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.display}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Near Vision */}
      <div className="mb-6">
        <h3 className="text-lg font-medium mb-3 text-gray-700">Near Vision Assessment</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">Right Eye</label>
            <select
              value={formData[`near_vision_right_${suffix}`]}
              onChange={(e) => handleInputChange(`near_vision_right_${suffix}`, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {nearVisionOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.display}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">Left Eye</label>
            <select
              value={formData[`near_vision_left_${suffix}`]}
              onChange={(e) => handleInputChange(`near_vision_left_${suffix}`, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {nearVisionOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.display}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Refraction - Right Eye */}
      <div className="mb-6">
        <h3 className="text-lg font-medium mb-3 text-gray-700">Refraction - Right Eye</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">Spherical</label>
            <select
              value={formData[`spherical_right_${suffix}`]}
              onChange={(e) => handleInputChange(`spherical_right_${suffix}`, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {sphericalOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.display}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">Cylindrical</label>
            <select
              value={formData[`cylindrical_right_${suffix}`]}
              onChange={(e) => handleInputChange(`cylindrical_right_${suffix}`, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {cylindricalOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.display}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">Axis</label>
            <select
              value={formData[`axis_right_${suffix}`]}
              onChange={(e) => handleInputChange(`axis_right_${suffix}`, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {axisOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.display}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">Add</label>
            <select
              value={formData[`add_right_${suffix}`]}
              onChange={(e) => handleInputChange(`add_right_${suffix}`, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {addOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.display}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Refraction - Left Eye */}
      <div>
        <h3 className="text-lg font-medium mb-3 text-gray-700">Refraction - Left Eye</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">Spherical</label>
            <select
              value={formData[`spherical_left_${suffix}`]}
              onChange={(e) => handleInputChange(`spherical_left_${suffix}`, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {sphericalOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.display}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">Cylindrical</label>
            <select
              value={formData[`cylindrical_left_${suffix}`]}
              onChange={(e) => handleInputChange(`cylindrical_left_${suffix}`, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {cylindricalOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.display}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">Axis</label>
            <select
              value={formData[`axis_left_${suffix}`]}
              onChange={(e) => handleInputChange(`axis_left_${suffix}`, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {axisOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.display}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">Add</label>
            <select
              value={formData[`add_left_${suffix}`]}
              onChange={(e) => handleInputChange(`add_left_${suffix}`, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {addOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.display}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  if (!patientId || !patientName) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="bg-red-50 border border-red-300 p-6 rounded mb-6">
            <p className="text-red-800 font-semibold">Error</p>
            <p className="text-red-600">Missing patient information. Please go back and select a patient.</p>
            <button
              onClick={handleBack}
              className="mt-4 bg-gray-700 text-white px-4 py-2 rounded hover:bg-gray-800"
            >
              ← Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">TATA1MG Optometry Test</h1>
            <p className="text-gray-600 mt-1">
              Patient: {patientName} | ID: {patientId}
              {technicianId && (
                <span className="ml-4 text-sm text-gray-500">
                  Technician ID: {technicianId}
                </span>
              )}
            </p>
          </div>
          <button
            onClick={handleBack}
            className="bg-gray-700 text-white px-4 py-2 rounded hover:bg-gray-800"
          >
            ← Back
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-300 p-4 rounded mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-300 p-4 rounded mb-6">
            <p className="text-green-800">{success}</p>
          </div>
        )}

        <div className="space-y-8">
          {/* WITHOUT GLASSES Section */}
          {renderVisionSection("Assessment WITHOUT Glasses", "wog", "bg-blue-50")}

          {/* WITH GLASSES Section */}
          {renderVisionSection("Assessment WITH Glasses", "wg", "bg-green-50")}

          {/* Color Vision */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Color Vision Assessment</h2>
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="color_vision_normal"
                  checked={formData.color_vision_normal}
                  onChange={(e) => handleInputChange("color_vision_normal", e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
                <label htmlFor="color_vision_normal" className="ml-2 text-sm font-medium text-gray-700">
                  Color Vision Normal
                </label>
              </div>
              
              {!formData.color_vision_normal && (
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Other Color Vision Issues
                  </label>
                  <textarea
                    value={formData.color_vision_other}
                    onChange={(e) => handleInputChange("color_vision_other", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Describe any color vision abnormalities..."
                  />
                </div>
              )}

              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Optometrist Remark
                </label>
                <textarea
                  value={formData.color_vision_remark}
                  onChange={(e) => handleInputChange("color_vision_remark", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  placeholder="Optometrist's remarks on color vision test..."
                />
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-center">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? "Saving..." : "Save Optometry Data"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Tata1MGOptometryForm;